import debug from 'Helper/debugHelpers'
import globalConfig from 'Helper/configHelpers'
import { callbackWrapper } from 'actions/apiActions'
import { put, take, fork, call } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'
import { prepStoreAction } from './xtractAPISagas'
import { get } from 'Helper/xtract_api'
import Store from '../store'
import defaultCallbacksImport from './defaultCallback'

export default function * APIstream(setup) {
  const { token, resource, method, id, obj, action } = setup

  const { callbacks = {} } = action
  const { api_base_url: API_BASE_URL } = globalConfig
  let stream
  try {
    debug.logObject(
      'making ' +
        method +
        ' API call to ' +
        API_BASE_URL +
        '/' +
        resource +
        ' with id = ' +
        id,
      obj,
      debug.logLevels.API
    )
    stream = yield call(get, token, resource, method, id, obj)
  } catch (error) {
    // some error
    // Need to catch 401 unauth and redirect to login
    debug.log('error: ' + error, debug.logLevels.API, 'error')
    // no fail over retry for streams, just return -1 so user can fail over
    // to polling implementation
    stream = -1
  }
  if (stream === -1) {
    if (callbacks[500] != null) {
      callbacks[500]()
    } else if (callbacks.error != null) {
      callbacks.error()
    }
  } else {
    const { resourceName } = prepStoreAction(resource, method)
    const isWaitlistStream =
      resourceName === 'WAITLIST' || resourceName === 'CLINIC_WAITLIST'
    if (isWaitlistStream && !resource.includes('last_updated_at')) {
      // A stream opened without a resume timestamp starts with the complete
      // list of waiting patients. The server never reports patients that left
      // while no stream was open, so the lists must be emptied first or those
      // patients would stay on screen.
      yield put({ type: 'RESET_WAITLIST_LISTS' })
    }
    yield fork(
      [this, streamInstance],
      stream,
      resourceName,
      method,
      action,
      callbacks
    )
    yield put({
      type: 'OPEN_STREAM',
      name: resourceName,
      id: id,
      result: stream
    })
    if (callbacks[200] != null) {
      callbacks[200](stream)
    }
  }
  return stream
}

function * streamInstance(stream, resourceName, method, action, callbacks) {
  debug.log('Stream opened.', debug.logLevels.API)
  const chan = eventChannel(emit =>
    channelEmitter(emit, stream, resourceName, method, callbacks)
  )

  try {
    let last_updated_at = null
    while (true) {
      const chanAction = yield take(chan)
      if (chanAction.type === undefined) {
        stream.close()
        chan.close()
        break
      } else if (chanAction.type !== 'status') {
        // dispatch the action
        if(chanAction.result && chanAction.result.length > 0 && chanAction.result[chanAction.result.length - 1]?.updated_at) {
          last_updated_at = chanAction.result[chanAction.result.length - 1].updated_at
        }
        yield put(chanAction)
      } else {
        // handle the status
        let response = {}
        switch (chanAction.result) {
          case undefined:
            debug.log(
              `Stream responded with status ${chanAction.result}. This is probably wake up from sleep. Trying to reconnect.`,
              debug.logLevels.API,
              'warn'
            )
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            chan.close()
            yield put(action) // resubmit stream request
            break
          case 500:
            debug.log(
              `Stream responded with status ${chanAction.result}. Fall back to polling.`,
              debug.logLevels.API,
              'error'
            )
            if (callbacks[500] != null) {
              callbacks[500]()
            } else if (callbacks.error != null) {
              callbacks.error()
            }
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            break
          case 401:
            debug.log(
              'Stream saw 401, closing stream and refreshing token.',
              debug.logLevels.API
            )
            // this is bad but i'm basically faking a response that looks
            // like a regular api call so that I can reuse the default
            // 401 callback that just retries if it saw 401 for unauth.
            response = { data: { message: 'unauthenticated.' } }
            yield call(defaultCallbacksImport[401], response, action, () =>
              callbackWrapper(defaultCallbacksImport[401], arguments)
            )
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            break
          case 429:
            // handled elsewhere
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            chan.close()
            break
          case 503:
            // the site is down so give up and logout
            debug.log('Stream received site 503 service unavailable. Doing logout.',debug.logLevels.AUTH, 'error')
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            chan.close()
            yield put(doLogout())
            break
          case 'event_lost_detected':
            // note updated_at still includes the last successfully received event updated_at timestamp
            // so this is a safe place to request resuming from.
            // intentional fall through to default case.
          default:
            debug.log(
              `Stream error: ${chanAction.result}, attempting stream restart...`,
              debug.logLevels.API,
              chanAction.result === 'closed' ? 'info' : 'error'
            )
            // this means the stream was previously established
            // so we should probably just try restarting the stream.
            chan.close()
            yield put({ type: 'CLOSE_STREAM', name: resourceName })
            // resubmit the request including the last_update_at
            let { resource } = action
            if(last_updated_at) {
              const parts = resource.split('?')
              resource = parts[0]+`?last_updated_at=${last_updated_at}`
            }

            yield put({...action, resource}) // resubmit stream request
        }
      }
    }
  } catch (error) {
    debug.logObject(
      'stream error encountered',
      error,
      debug.logLevels.API,
      'error'
    )

    chan.close()
    yield put({ type: 'CLOSE_STREAM', name: resourceName })
  }
}

const channelEmitter = (
  emit,
  stream,
  resourceName,
  method,
  userCallbacks = []
) => {
  let lastEventId = null
  stream.onmessage = function(e) {
    let data = JSON.parse(e.data)
    const currentEventId = parseInt(e.lastEventId);
    const expectedEventId = lastEventId !== null ? parseInt(lastEventId) + 1 : null;
    if(expectedEventId !== null && currentEventId !== expectedEventId) {
      // gap detected
      emit({ type: 'status', result: 'event_lost_detected' })
      emit(END)
    } else {
      // if lastEventId is null its the first event so no gap detection yet
      // if lastEventId matches e.lastEventId -1 then no gap
      lastEventId = e.lastEventId
    }
    if (data.event === 'update') {
      emit({
        type: 'STORE_' + resourceName + '_' + method.toUpperCase(),
        result: data.data
      })
      if (userCallbacks.stream_update != null) {
        Store.dispatch(
          callbackWrapper(userCallbacks.stream_update, arguments)
        )
      }
    }
    e = null // trying to force garbage collection. likely useless
    data = null
  }
  stream.onerror = function(e) {
    const status = e.status
    // make a duplicate so we can maintain a list of the default callbacks
    const defaultCallbacks = { ...defaultCallbacksImport }
    // now override the default callback with any user provided callback for the same key

    if (typeof userCallbacks[status] === 'function') { defaultCallbacks[status] = userCallbacks[status] } else if (([200, 201]).indexOf(status) !== -1 && typeof userCallbacks.success === 'function') {
      defaultCallbacks[status] = userCallbacks.success
    } else if (([400, 422]).indexOf(status) !== -1 && typeof userCallbacks.validation === 'function') {
      defaultCallbacks[status] = userCallbacks.validation
    } else if (([401, 403, 419, 404, 429, 500]).indexOf(status) !== -1 && typeof userCallbacks.error === 'function') {
      defaultCallbacks[status] = userCallbacks.error
    } else if (([401, 403, 419, 404, 429, 500]).indexOf(status) !== -1 && typeof userCallbacks.failure === 'function') {
      defaultCallbacks[status] = userCallbacks.failure
    } else if (typeof userCallbacks.error === 'function') {
      defaultCallbacks.default = userCallbacks.error
    } else if (typeof userCallbacks.failure === 'function') {
      defaultCallbacks.default = userCallbacks.failure
    }
    if (e.readyState != EventSource.CLOSED) {
      // An error occured probably auth related
      debug.logObject('Stream closed by server:', e, debug.logLevels.API)
      emit({ type: 'status', result: e.status || 'closed' })
      if (e.status === 429) {
        // its been handled.
        Store.dispatch(
          callbackWrapper(defaultCallbacks[429], arguments)
        )
        emit(END)
      } else if (defaultCallbacks.stream_closed != null) {
        Store.dispatch(
          callbackWrapper(defaultCallbacks.stream_closed, arguments)
        )
      }
    } else {
      debug.logObject(
        'Stream error but stream state is not closed?',
        e,
        debug.logLevels.API,
        'error'
      )
      emit(END)
      if (defaultCallbacks.stream_error != null) {
        Store.dispatch(
          callbackWrapper(defaultCallbacks.stream_error, arguments)
        )
      }
    }
    e = null // trying to force garbage collection. likely useless
    stream = null
  }

  // This subscriber function must return an unsubscribe function
  return () => {
    stream.close()
  }
}
